import React, { useMemo, useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Activity,
  ChevronLeft,
  Menu,
  Mail,
  Receipt,
  AppWindow,
  Users,
  X,
  DollarSign,
  Shield,
  Briefcase,
  FileText,
  Shapes,
  Database,
  FilePlus2,
  Files,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  CreditCard,
  AlertTriangle,
  ShieldCheck,
  Bot,
} from 'lucide-react';
import { NavItem } from '@/types';
import { useAuth } from '@/features/auth/contexts/AuthContext';

interface SidebarProps {
  isDarkMode: boolean;
  isCollapsed: boolean;
  isMobileMenuOpen: boolean;
  toggleSidebar: () => void;
  closeMobileMenu: () => void;
  onNavigate: (path: string) => void;
  currentPath: string;
}

const APP_SLUG = 'rolling_skeleton';
const CONTENT_PERMISSION_KEY = 'content_management';

const Sidebar: React.FC<SidebarProps> = ({
  isDarkMode,
  isCollapsed,
  isMobileMenuOpen,
  toggleSidebar,
  closeMobileMenu,
  onNavigate,
  currentPath
}) => {
  const { user } = useAuth();
  // Track expanded parent items
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  const isPathActive = (path?: string) => {
    if (!path) return false;
    if (currentPath === path) return true;
    if (path === '/content/schemas') return /^\/content\/schemas\/[^/]+$/.test(currentPath);
    if (path === '/content/contents') return /^\/content\/contents\/[^/]+$/.test(currentPath);
    if (path === '/legal/public') return currentPath.startsWith('/legal/public');
    return false;
  };

  // Define the structure
  const rawNavItems: NavItem[] = useMemo(() => [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/' },
    {
      id: 'finance',
      label: 'Finance',
      icon: DollarSign,
      children: [
        { id: 'invoicing', label: 'Invoicing Service', icon: Receipt, path: '/invoices' },
        { id: 'subscriptions', label: 'Subscriptions', icon: RefreshCw, path: '/subscriptions' },
        { id: 'payment-apps', label: 'Payment Apps', icon: CreditCard, path: '/payment-apps' },
        { id: 'webhook-dl', label: 'Webhook Dead Letter', icon: AlertTriangle, path: '/webhook-dead-letter' },
        { id: 'compliance', label: 'Compliance', icon: ShieldCheck, path: '/compliance' }
      ]
    },
    {
      id: 'sso',
      label: 'SSO & IdP',
      icon: Shield,
      children: [
        { id: 'user-mgmt', label: 'User Management', icon: Users, path: '/users' },
        { id: 'apps-mgmt', label: 'Apps Management', icon: AppWindow, path: '/apps' },
        { id: 'service-agents', label: 'Service Agents', icon: Bot, path: '/service-agents' }
      ]
    },
    {
      id: 'monitoring',
      label: 'Log & Monitoring',
      icon: Activity,
      children: [
        { id: 'llm-logger', label: 'LLM API Logger', icon: FileText, path: '/llm-logger' }
      ]
    },
    {
      id: 'content-management',
      label: 'Content Management',
      icon: Shapes,
      children: [
        { id: 'schema-registry', label: 'Schema Registry', icon: Database, path: '/content/schemas' },
        { id: 'schema-create', label: 'New Schema', icon: FilePlus2, path: '/content/schemas/new' },
        { id: 'content-list', label: 'Content Records', icon: Files, path: '/content/contents' },
        { id: 'content-create', label: 'New Content', icon: FilePlus2, path: '/content/contents/new' }
      ]
    },
    {
      id: 'legal',
      label: 'Legal',
      icon: FileText,
      children: [
        { id: 'public-legal', label: 'Public Legal', icon: Files, path: '/legal/public' }
      ]
    },
    {
      id: 'utilities',
      label: 'Utilities',
      icon: Briefcase,
      children: [
        { id: 'mail-notifier', label: 'Mail Notifier', icon: Mail, path: '/mail-notifier' }
      ]
    }
  ], []);

  // Filter items based on permissions
  const menuItems = useMemo(() => {
    if (!user) return [];

    const hasPermission = (itemId: string) => {
      if (itemId === 'dashboard') return true;
      if (user.is_superuser) return true;
      const permissionKey = (
        itemId === 'schema-registry' ||
        itemId === 'schema-create' ||
        itemId === 'content-list' ||
        itemId === 'content-create' ||
        itemId === 'public-legal'
      ) ? CONTENT_PERMISSION_KEY : itemId;
      // Check permission map
      const appPerms = user.app_permissions?.[APP_SLUG] ?? user.permissions?.[APP_SLUG];
      return appPerms && appPerms[permissionKey] === true;
    };

    const filterItems = (items: NavItem[]): NavItem[] => {
      return items.reduce((acc: NavItem[], item) => {
        // If item has children, filter them first
        if (item.children) {
          const visibleChildren = filterItems(item.children);
          if (visibleChildren.length > 0) {
            acc.push({ ...item, children: visibleChildren });
          }
        } else {
          // Leaf node - check permission
          if (hasPermission(item.id)) {
            acc.push(item);
          }
        }
        return acc;
      }, []);
    };

    return filterItems(rawNavItems);
  }, [user, rawNavItems]);

  // Automatically expand groups that contain the active path
  useEffect(() => {
    const newExpanded: Record<string, boolean> = { ...expandedItems };
    let changed = false;

    menuItems.forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(child => isPathActive(child.path));
        if (hasActiveChild && !newExpanded[item.id]) {
          newExpanded[item.id] = true;
          changed = true;
        }
      }
    });

    if (changed) {
      setExpandedItems(newExpanded);
    }
  }, [currentPath, menuItems]);

  const toggleGroup = (id: string) => {
    if (isCollapsed) {
      toggleSidebar(); // Expand sidebar if clicking a group in collapsed mode
      setExpandedItems(prev => ({ ...prev, [id]: true })); // Ensure it opens
    } else {
      setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
    }
  };

  const handleItemClick = (item: NavItem) => {
    if (item.children) {
      toggleGroup(item.id);
    } else if (item.path) {
      onNavigate(item.path);
      // On mobile, close menu after navigation
      if (window.innerWidth < 768) {
        closeMobileMenu();
      }
    }
  };

  const renderMenuItem = (item: NavItem, depth = 0) => {
    const isActive = isPathActive(item.path);
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems[item.id];

    // Check if any child is active to highlight parent
    const isChildActive = hasChildren && item.children?.some(c => isPathActive(c.path));

    // Base padding for depth
    const paddingLeft = depth > 0 ? (isCollapsed ? 0 : 2.5) : 1;

    return (
      <div key={item.id} className="w-full relative group">
        <button
          onClick={() => handleItemClick(item)}
          className={`
            w-[calc(100%-2rem)] mx-4 mb-2 flex items-center transition-all duration-300 rounded-lg border
            ${isCollapsed ? 'justify-center px-2' : 'justify-between px-3'}
            py-3.5 font-montserrat text-sm font-medium group
            ${isActive
              ? 'bg-[#0030FF]/10 dark:bg-[#0030FF]/20 border-[#0030FF]/30 dark:border-[#0030FF]/50 text-[#0030FF] dark:text-white'
              : isChildActive
                ? 'text-slate-800 dark:text-white border-transparent'
                : 'text-slate-500 dark:text-slate-400 border-transparent hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-800 dark:hover:text-white'
            }
          `}
          style={{ paddingLeft: !isCollapsed && depth > 0 ? `${paddingLeft}rem` : undefined }}
          title={isCollapsed ? item.label : undefined}
        >
          <div className={`flex items-center ${isCollapsed ? 'justify-center w-full' : ''} gap-3 md:gap-3`}>
            <div className={`transition-colors duration-300 ${isActive ? 'text-[#0030FF] dark:text-orionYellow' : 'text-slate-400 group-hover:text-slate-800 dark:group-hover:text-white'}`}>
              {item.icon && <item.icon size={isCollapsed ? 22 : 18} />}
            </div>
            <span className={`${isCollapsed ? 'hidden' : 'block'} tracking-wide`}>{item.label}</span>
          </div>

          {!isCollapsed && hasChildren && (
            <div className="text-slate-400 dark:text-slate-500">
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </div>
          )}
        </button>

        {/* Floating Tooltip for collapsed mode (Desktop only) */}
        {isCollapsed && (
          <div className="hidden md:block absolute left-full top-0 ml-2 p-2 bg-white dark:bg-slate-800 text-slate-800 dark:text-white shadow-lg border border-slate-200 dark:border-slate-700 text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
            {item.label}
          </div>
        )}

        {/* Render Children */}
        {!isCollapsed && hasChildren && isExpanded && (
          <div className="bg-slate-50/50 dark:bg-slate-800/20 mt-1 mb-2 rounded-xl mx-2 overflow-hidden py-1">
            {item.children?.map(child => renderMenuItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`
            fixed inset-y-0 left-0 z-50 md:z-20
            bg-white dark:bg-[#0a0a2a] border-r border-slate-200/60 dark:border-slate-800/80 shadow-2xl dark:shadow-none
            flex flex-col transition-all duration-300 ease-in-out
            ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            ${isCollapsed ? 'md:w-20' : 'md:w-72'}
            w-72 overflow-hidden
        `}
    >
      <div className={`h-16 flex items-center justify-between px-6 border-b border-slate-200/60 dark:border-slate-800/80 transition-all duration-300 ${isCollapsed ? 'md:justify-center md:px-0' : ''}`}>
        <div className={`flex items-center gap-2 ${isCollapsed ? 'md:hidden' : ''}`}>
          <img
            src={isDarkMode
              ? "https://storage.orionex.id/orionex-storage/orionex_tiny_white.png"
              : "https://storage.orionex.id/orionex-storage/orionex.webp"
            }
            alt="Orionex"
            className="h-8 object-contain transition-opacity duration-200"
          />
        </div>

        {/* Desktop Collapse Toggle */}
        <button
          onClick={toggleSidebar}
          className="hidden md:block p-1 rounded-md text-slate-400 hover:text-orionBlue hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors"
        >
          {isCollapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
        </button>

        {/* Mobile Close Button */}
        <button
          onClick={closeMobileMenu}
          className="md:hidden p-1 rounded-md text-slate-400 hover:text-red-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-4 scrollbar-hide overflow-x-hidden">
        {!isCollapsed && (
          <div className="px-6 py-4 mb-2 text-[10px] font-montserrat font-bold text-slate-500 tracking-widest uppercase">
            Admin Services
          </div>
        )}
        {menuItems.map(item => renderMenuItem(item))}
      </div>

      <div className="p-6 relative z-20">
        <div className={`bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/50 rounded-xl p-4 flex items-center gap-3 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800/60 cursor-pointer ${isCollapsed ? 'justify-center p-2' : ''}`}>
          {user?.picture ? (
            <img
              src={user.picture}
              alt="User"
              className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-600 object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-[#0030FF]/10 dark:bg-[#0030FF]/20 border border-[#0030FF]/30 dark:border-[#0030FF]/50 text-[#0030FF] dark:text-orionYellow flex items-center justify-center font-bold text-xs shrink-0">
              {user?.full_name ? user.full_name[0].toUpperCase() : user?.email?.[0].toUpperCase()}
            </div>
          )}

          <div className={`flex flex-col overflow-hidden whitespace-nowrap ${isCollapsed ? 'hidden' : 'block'}`}>
            <span className="text-sm font-montserrat font-bold text-slate-800 dark:text-white truncate max-w-[140px]" title={user?.full_name || user?.email}>
              {user?.full_name || user?.email?.split('@')[0] || 'User'}
            </span>
            <span className="text-[10px] text-slate-500 dark:text-slate-400">
              {user?.is_superuser ? 'System Administrator' : 'Staff'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
